import { useEffect, useState } from "react";
import { useShowContext } from "react-admin";
import {
  Box,
  Button,
  Paper,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";

import {
  CustomTableRowTR,
  StatusBadge,
  StyledTable,
  TableData,
  TableHeader,
  TableTopHeader,
  TableWrapper,
  Title,
} from "../../styles/commonStyles";

import ChefForPartyService from "../../services/ChefForPartyService";
import EventBus from "../../EventBus";
import { EventBusEventsEnum } from "../../enums/EventBusEventsEnum";
import { CustomSnackbarTypes } from "../Common/CustomSnackBar";
import { BookByDishCountUnit } from "../../enums/BookByDishCountUnit";
import { lineQuantityText } from "../../utils/BookByDishPlate";

// A proposal row carries two counts whose units can differ: a booking placed on a pre-plate
// build holds pieces, while a change proposed from a plate-aware build is plates. Each side is
// rendered from its own unit fields, never from the dish.
const currentQuantityText = (row: any): string =>
  lineQuantityText(
    { count: row.currentCount, countUnit: row.currentCountUnit, plateSize: row.currentPlateSize, pieces: row.currentPieces },
    { plateUnit: row.plateUnit },
  );

const proposedQuantityText = (row: any): string =>
  lineQuantityText(
    { count: row.proposedCount, countUnit: row.proposedCountUnit, plateSize: row.proposedPlateSize, pieces: row.proposedPieces },
    { plateUnit: row.plateUnit },
  );

// `delta` is proposed - current in each side's own unit, so it is meaningless across a unit
// switch (2 plates vs 8 pieces). A newly added (currentCount 0) or removed (proposedCount 0)
// dish has no unit conflict.
const unitWord = (countUnit?: string): string =>
  countUnit === BookByDishCountUnit.PLATE ? "plates" : "pcs";

const isUnitSwitch = (row: any): boolean =>
  Number(row.currentCount) > 0 &&
  Number(row.proposedCount) > 0 &&
  (row.currentCountUnit ?? BookByDishCountUnit.PIECE) !== (row.proposedCountUnit ?? BookByDishCountUnit.PIECE);

const chefForPartyService = new ChefForPartyService();

const mealVariantMap: any = {
  STARTER: "starter",
  "MAIN COURSE": "mainCourse",
  DESSERT: "dessert",
  SIDE_DISH: "sideDish",
  BEVERAGE: "beverage",
};

const BookByDishProposals = () => {
  const { record } = useShowContext();
  const [proposals, setProposals] = useState<any[]>([]);
  const [loadingAction, setLoadingAction] = useState(false);

  const fetchProposals = () => {
    if (!record?.id) return;
    chefForPartyService
      .getBookByDishProposals(Number(record.id))
      .then((data) => setProposals(Array.isArray(data) ? data : []))
      .catch(() => setProposals([]));
  };

  useEffect(() => {
    fetchProposals();
    // eslint-disable-next-line
  }, [record?.id]);

  const handleApprove = () => {
    EventBus.emitEvent(EventBusEventsEnum.SHOW_CONFIRMATION_DIALOG, {
      title: "Accept Proposals?",
      message: "Are you sure you want to accept all pending proposals?",
      successLabel: "Accept",
      failureLabel: "Cancel",
      onClose: (confirmed: boolean) => {
        if (!confirmed) return;
        setLoadingAction(true);
        chefForPartyService
          .approveBookByDishProposals(Number(record.id))
          .then(() => {
            EventBus.emitEvent(EventBusEventsEnum.SNACKBAR_SHOW, {
              message: "Proposals accepted successfully",
              type: CustomSnackbarTypes.SUCCESS,
            });
            fetchProposals();
          })
          .catch(() => {
            EventBus.emitEvent(EventBusEventsEnum.SNACKBAR_SHOW, {
              message: "Failed to accept proposals",
              type: CustomSnackbarTypes.ERROR,
            });
          })
          .finally(() => setLoadingAction(false));
      },
    });
  };

  const handleReject = () => {
    EventBus.emitEvent(EventBusEventsEnum.SHOW_CONFIRMATION_DIALOG, {
      title: "Reject Proposals?",
      message: "Are you sure you want to reject all pending proposals?",
      successLabel: "Reject",
      failureLabel: "Cancel",
      onClose: (confirmed: boolean) => {
        if (!confirmed) return;
        setLoadingAction(true);
        chefForPartyService
          .rejectBookByDishProposals(Number(record.id))
          .then(() => {
            EventBus.emitEvent(EventBusEventsEnum.SNACKBAR_SHOW, {
              message: "Proposals rejected successfully",
              type: CustomSnackbarTypes.SUCCESS,
            });
            fetchProposals();
          })
          .catch(() => {
            EventBus.emitEvent(EventBusEventsEnum.SNACKBAR_SHOW, {
              message: "Failed to reject proposals",
              type: CustomSnackbarTypes.ERROR,
            });
          })
          .finally(() => setLoadingAction(false));
      },
    });
  };

  return (
    <TableWrapper>
      <TableTopHeader>
        <Title>Proposals</Title>
        {proposals.length > 0 && (
          <Box sx={{ display: "flex", gap: 1 }}>
            <Button
              size="small"
              variant="contained"
              color="success"
              disabled={loadingAction}
              onClick={handleApprove}
            >
              Accept
            </Button>
            <Button
              size="small"
              variant="contained"
              color="error"
              disabled={loadingAction}
              onClick={handleReject}
            >
              Reject
            </Button>
          </Box>
        )}
      </TableTopHeader>

      {proposals.length === 0 ? (
        <Typography
          sx={{ color: "#6C7C93", fontSize: "14px", px: "24px", py: "12px" }}
        >
          No proposals found
        </Typography>
      ) : (
        <Box sx={{ px: "24px", pb: "16px" }}>
          <TableContainer component={Paper} sx={{ borderRadius: "8px" }}>
            {/* Auto layout: plate quantities ("2 Plates · 16 pcs") and unit switches in Delta are
                long, so columns size to their content; the container scrolls if it overflows. */}
            <StyledTable>
              <TableHead>
                <TableRow
                  sx={{
                    backgroundColor: "rgba(232, 235, 237, 0.3) !important",
                  }}
                >
                  <TableHeader>#</TableHeader>
                  <TableHeader>Name</TableHeader>
                  <TableHeader style={{ textAlign: "center" }}>Type</TableHeader>
                  <TableHeader style={{ textAlign: "center" }}>
                    Meal Type
                  </TableHeader>
                  <TableHeader style={{ textAlign: "center" }}>
                    Current
                  </TableHeader>
                  <TableHeader style={{ textAlign: "center" }}>
                    Proposed
                  </TableHeader>
                  <TableHeader style={{ textAlign: "center" }}>Delta</TableHeader>
                  <TableHeader style={{ textAlign: "center" }}>Price</TableHeader>
                  <TableHeader style={{ textAlign: "center" }}>
                    Time (min)
                  </TableHeader>
                </TableRow>
              </TableHead>
              <TableBody>
                {proposals.map((row: any, index: number) => (
                  <CustomTableRowTR key={row.id}>
                    <TableData>{index + 1}</TableData>
                    <TableData style={{ whiteSpace: "normal", minWidth: "160px" }}>
                      {row.dishName}
                    </TableData>
                    <TableData style={{ textAlign: "center" }}>
                      <StatusBadge
                        variant={row.dishType === "VEG" ? "veg" : "nonVeg"}
                      >
                        {row.dishType}
                      </StatusBadge>
                    </TableData>
                    <TableData style={{ textAlign: "center" }}>
                      <StatusBadge variant={mealVariantMap[row.mealType]}>
                        {row.mealType}
                      </StatusBadge>
                    </TableData>
                    <TableData style={{ textAlign: "center" }}>
                      {currentQuantityText(row)}
                    </TableData>
                    <TableData style={{ textAlign: "center" }}>
                      {proposedQuantityText(row)}
                    </TableData>
                    <TableData
                      style={{
                        textAlign: "center",
                        fontWeight: row.delta !== 0 && !isUnitSwitch(row) ? 700 : 400,
                        color:
                          isUnitSwitch(row)
                            ? undefined
                            : row.delta > 0
                            ? "#16a34a"
                            : row.delta < 0
                              ? "#dc2626"
                              : undefined,
                      }}
                    >
                      {isUnitSwitch(row)
                        ? `${unitWord(row.currentCountUnit)} → ${unitWord(row.proposedCountUnit)}`
                        : row.delta > 0 ? `+${row.delta}` : row.delta}
                    </TableData>
                    <TableData style={{ textAlign: "center" }}>
                      ₹{row.projectedPrice}
                    </TableData>
                    <TableData style={{ textAlign: "center" }}>
                      {row.projectedTime}
                    </TableData>
                  </CustomTableRowTR>
                ))}
              </TableBody>
            </StyledTable>
          </TableContainer>
        </Box>
      )}
    </TableWrapper>
  );
};

export default BookByDishProposals;
